import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';

export default function VerifyEmail() {
  const { currentUser, isEmailVerified: checkEmailVerified } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [codeSent, setCodeSent] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const codeSentRef = useRef(false); // Use ref to track if code was sent

  // Check if user just signed up
  const justSignedUp = location.state?.justSignedUp;
  const signupEmail = location.state?.email;

  // Debug component lifecycle
  useEffect(() => {
    console.log('🔄 VerifyEmail component mounted:', {
      justSignedUp,
      signupEmail,
      currentUserUid: currentUser?.uid,
      currentUserEmail: currentUser?.email,
      locationState: location.state
    });
  }, [justSignedUp, signupEmail, currentUser?.uid, currentUser?.email, location.state]);

  // Handle resend countdown
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setResendDisabled(false);
    }
  }, [resendCountdown]);

  // Send verification code when component mounts
  useEffect(() => {
    console.log('🔍 useEffect triggered:', {
      currentUserUid: currentUser?.uid,
      codeSentRef: codeSentRef.current,
      isEmailVerified,
      verificationSuccess,
      shouldSend: currentUser?.uid && !codeSentRef.current && !isEmailVerified && !verificationSuccess
    });
    
    if (currentUser?.uid && !codeSentRef.current && !isEmailVerified && !verificationSuccess) {
      // Check if user is already verified in Firestore before sending code
      const checkIfAlreadyVerified = async () => {
        try {
          console.log('🔍 Checking if user is already verified before sending code...');
          const isVerified = await checkEmailVerified(currentUser.uid);
          
          if (isVerified) {
            console.log('✅ User is already verified, skipping code send');
            setIsEmailVerified(true);
            setVerificationSuccess(true);
            return;
          }
          
          console.log('📤 Sending initial verification code for user:', currentUser.uid);
          sendVerificationCode();
        } catch (error) {
          console.error('Error checking verification status:', error);
          // If check fails, still send the code
          console.log('📤 Sending verification code (fallback):', currentUser.uid);
          sendVerificationCode();
        }
      };
      
      checkIfAlreadyVerified();
    }
  }, [currentUser?.uid, isEmailVerified, verificationSuccess]); // Add verificationSuccess to dependencies

  // Reset verification state when user changes
  useEffect(() => {
    console.log('🔄 User changed, resetting verification state:', {
      previousUser: currentUser?.uid,
      isEmailVerified: isEmailVerified,
      codeSentRef: codeSentRef.current,
      verificationSuccess
    });
    
    // Check if user is already verified before resetting
    const checkAndReset = async () => {
      try {
        const isVerified = await checkEmailVerified(currentUser.uid);
        if (isVerified) {
          console.log('✅ User is already verified, keeping verification flags');
          setIsEmailVerified(true);
          setVerificationSuccess(true);
          return;
        }
        
        // Only reset if user is not verified
        console.log('🔄 User not verified, resetting flags');
        setIsEmailVerified(false);
        setVerificationSuccess(false);
        codeSentRef.current = false;
        console.log('✅ Reset complete - isEmailVerified: false, verificationSuccess: false, codeSentRef: false');
      } catch (error) {
        console.error('Error checking verification status during reset:', error);
        // If check fails, reset flags as fallback
        setIsEmailVerified(false);
        setVerificationSuccess(false);
        codeSentRef.current = false;
      }
    };
    
    if (currentUser?.uid) {
      checkAndReset();
    }
  }, [currentUser?.uid]);

  const sendVerificationCode = async () => {
    try {
      console.log('🚀 sendVerificationCode called');
      setLoading(true);
      setError('');
      
      // Check if user is authenticated and has email
      if (!currentUser) {
        setError('No authenticated user found. Please log in again.');
        return;
      }
      
      if (!currentUser.email) {
        setError('No email address found for user. Please log in again.');
        return;
      }
      
      // Debug logging (without showing any sensitive data)
      console.log('Sending verification code request:', {
        email: currentUser.email,
        userId: currentUser.uid
      });
      
      // Call Firebase function to send email (code will be generated server-side)
      const functions = getFunctions();
      const sendCode = httpsCallable(functions, 'sendVerificationCode');
      
      const result = await sendCode({
        email: currentUser.email
      });
      
      console.log('Function call result:', result);
      
      setCodeSent(true);
      codeSentRef.current = true; // Mark as sent
      console.log('✅ Code sent successfully, codeSentRef set to true');
      setMessage('Verification code sent! Please check your email.');
      
      // Set resend cooldown
      setResendDisabled(true);
      setResendCountdown(60);
      
    } catch (err) {
      console.error('Error sending verification code:', err);
      console.error('Error details:', {
        code: err.code,
        message: err.message,
        details: err.details
      });
      setMessage('');
      setError(err.message || 'Failed to send verification code. Please try again.');
      // Let the user retry immediately when the send failed.
      codeSentRef.current = false;
      setResendDisabled(false);
      setResendCountdown(0);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (verificationSuccess) {
      console.log('🚫 Resend blocked - verification already succeeded');
      return;
    }
    await sendVerificationCode();
  };

  const handleCodeChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newCode = [...verificationCode];
      newCode[index] = value;
      setVerificationCode(newCode);
      
      // Auto-focus next input
      if (value && index < 5) {
        const nextInput = document.getElementById(`code-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleVerifyCode = async () => {
    const enteredCode = verificationCode.join('');
    
    console.log('Verification attempt:', {
      enteredCodeLength: enteredCode.length,
      currentUserEmail: currentUser?.email
    });
    
    if (enteredCode.length !== 6) {
      setError('Please enter the complete 6-digit code.');
      return;
    }
    
    try {
      console.log('🔐 Code entered, attempting to verify with server...');
      
      // Set verification success flag IMMEDIATELY to prevent further actions
      setVerificationSuccess(true);
      console.log('🎉 verificationSuccess set to true - preventing further actions');
      
      // Call Firebase function to verify the code
      const functions = getFunctions();
      const verifyCode = httpsCallable(functions, 'verifyCode');
      
      const result = await verifyCode({
        email: currentUser.email,
        code: enteredCode
      });
      
      console.log('Code verification result:', result);
      
      if (result.data.success) {
        // verifyCode writes verifiedEmails/{uid} server-side, so there is
        // nothing to mark from here. markEmailAsVerified is only for accounts
        // a provider already vouched for (Google), and calling it for an
        // email/password signup fails with "not verified by its sign-in
        // provider" - after the account had in fact just been verified.
        setIsEmailVerified(true);
        
        setMessage('Email verified successfully! Redirecting...');
        
        console.log('Redirecting to home page...');
        console.log('Current location before redirect:', window.location.href);
        
        // Force redirect to home page using window.location.replace
        console.log('About to execute redirect...');
        window.location.replace('/');
        console.log('Redirect command executed - this should not appear if redirect works');
        
        // Prevent any further execution
        return;
      } else {
        setMessage('');
        setError(result.data.error || 'Invalid verification code. Please try again.');
        setVerificationCode(['', '', '', '', '', '']);
        // Reset verification success flag if verification failed
        setVerificationSuccess(false);
      }
    } catch (error) {
      console.error('Error verifying code:', error);
      setMessage('');
      setError(error.message || 'Failed to verify code. Please try again.');
      setVerificationCode(['', '', '', '', '', '']);
      // Reset verification success flag if there was an error
      setVerificationSuccess(false);
    }
  };

  if (!currentUser) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4 py-8 pt-24">
      <div className="w-full max-w-md tiu-card p-8 text-center">
        <div className="mb-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h1 className="tiu-h1 mb-2">
            {justSignedUp ? 'Welcome to TeeItUp!' : 'Verify Your Email'}
          </h1>
          <p className="text-slate-600 text-sm">
            {justSignedUp ? (
              <>
                Your account has been created! We've sent a 6-digit verification code to{' '}
                <span className="font-semibold text-slate-900">{signupEmail || currentUser?.email}</span>
              </>
            ) : (
              <>
                Enter the 6-digit verification code sent to{' '}
                <span className="font-semibold text-slate-900">{currentUser?.email}</span>
              </>
            )}
          </p>
        </div>

        <div className="bg-blue-900 border border-blue-700 rounded-lg p-4 mb-6">
          <h3 className="text-blue-200 font-medium mb-2">📧 Verification Code:</h3>
          <div className="flex justify-center space-x-2 mb-4">
            {verificationCode.map((digit, index) => (
              <input
                key={index}
                id={`code-${index}`}
                type="text"
                maxLength="1"
                value={digit}
                onChange={(e) => handleCodeChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="w-12 h-12 text-center text-xl font-bold border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none bg-white text-gray-900"
                autoFocus={index === 0}
              />
            ))}
          </div>
          <button
            onClick={handleVerifyCode}
            disabled={verificationCode.join('').length !== 6}
            className="w-full tiu-btn tiu-btn-primary"
          >
            Verify Code
          </button>
        </div>

        {message && (
          <div className="tiu-alert-success mb-4">
            {message}
          </div>
        )}
        
        {error && (
          <div className="tiu-alert-error mb-4">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={handleResend}
            disabled={resendDisabled}
            className="w-full tiu-btn tiu-btn-secondary"
          >
            {resendDisabled 
              ? `Resend in ${resendCountdown}s` 
              : 'Resend Code'
            }
          </button>
        </div>
      </div>
    </div>
  );
} 